import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { purchasesApi } from "../../../api/inventory/purchases";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { PlusIcon, ShoppingCartIcon, ArrowTopRightOnSquareIcon, DocumentTextIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { Dialog } from "@headlessui/react";
import toast from "react-hot-toast";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import { fetchCustomers } from "../../../api/customer";
import { PMPagination } from "../../../components/utils/PMPagination";
import { formatDecimal, osloToday } from "./inventory-format";
import { clearPurchaseCreateAttempt, readPurchaseCreateAttempt, startPurchaseCreateAttempt } from "./purchase-create-attempt";

const statusColor = {
  draft: "bg-slate-100 text-slate-700", sent: "bg-blue-50 text-blue-800",
  confirmed: "bg-amber-50 text-amber-800", received: "bg-emerald-50 text-emerald-800",
  cancelled: "bg-rose-50 text-rose-800",
};
const formatDate = (value) => value ? new Intl.DateTimeFormat("nb-NO", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Oslo" }).format(new Date(value)) : "—";
const formatMoney = (value, currency = "NOK") => value == null ? "—" : `${formatDecimal(value)} ${currency}`;
const OPEN_STATUSES = ["sent", "confirmed"];

export const Purchases = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const companyId = useSelector((state) => state.session.currentCompany?.id);
  const activeCompanyRef = useRef(companyId);
  activeCompanyRef.current = companyId;
  const roles = useSelector((state) => state.session.currentUser?.roles || []);
  const canCreate = roles.some((role) => role.name === "admin" || role.name === "inventory_finance");
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("query") || "";
  const [page, setPage] = useState(Math.max(1, Number.parseInt(searchParams.get("page"), 10) || 1));
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const requestSeq = useRef(0);
  const supplierSeq = useRef(0);
  const [purchases, setPurchases] = useState([]);
  const [loadedCompanyId, setLoadedCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierSearchQuery, setSupplierSearchQuery] = useState("");
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [supplierError, setSupplierError] = useState(false);
  const [createPending, setCreatePending] = useState(false);
  const createInFlightCompany = useRef(null);
  const [createError, setCreateError] = useState(null);
  const [storedAttempt, setStoredAttempt] = useState(() => readPurchaseCreateAttempt(companyId));
  const activeAttempt = storedAttempt?.companyId === companyId ? storedAttempt : null;
  const [recoveryChecked, setRecoveryChecked] = useState(false);
  const [recoveryChecking, setRecoveryChecking] = useState(false);
  const [recoveryCandidates, setRecoveryCandidates] = useState([]);
  const [createDefinitelyRejected, setCreateDefinitelyRejected] = useState(false);
  const recoverySeq = useRef(0);
  const [filters, setFilters] = useState({
    query: initialQuery,
    statuses: searchParams.getAll("status"),
  });
  const [newPurchase, setNewPurchase] = useState({
    supplier_id: "",
    purchase_date: osloToday(),
    delivery_date: "",
    reference: "",
  });

  useEffect(() => {
    setCreateDialogOpen(false); setSuppliers([]); setSelectedSupplier(null); supplierSeq.current += 1;
    setCreateError(null); setCreatePending(false); setRecoveryChecked(false); setRecoveryCandidates([]); setCreateDefinitelyRejected(false); recoverySeq.current += 1;
    const saved = readPurchaseCreateAttempt(companyId);
    setStoredAttempt(saved);
    setNewPurchase(saved ? {
      supplier_id: String(saved.payload.supplier_id), purchase_date: saved.payload.purchase_date,
      delivery_date: saved.payload.delivery_date || "", reference: saved.payload.reference || "",
    } : { supplier_id: "", purchase_date: osloToday(), delivery_date: "", reference: "" });
    if (saved?.supplierName) setSelectedSupplier({ id: saved.payload.supplier_id, displayTitle: saved.supplierName });
  }, [companyId]);

  useEffect(() => {
    fetchPurchases();
    return () => { requestSeq.current += 1; };
  }, [filters, page, companyId]);

  // Fetch suppliers with search
  useEffect(() => {
    if (!createDialogOpen) return undefined;
    const timeoutId = setTimeout(() => {
      fetchSuppliers(supplierSearchQuery);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [supplierSearchQuery, companyId, createDialogOpen]);

  const fetchSuppliers = async (query = "") => {
    const current = ++supplierSeq.current;
    try {
      setLoadingSuppliers(true);
      setSupplierError(false);
      const params = {
        supplier: true, // Filter for suppliers using customer_type
        active: true,
      };
      if (query) {
        params.query = query;
      }
      const response = await fetchCustomers(params);
      // fetchCustomers returns response.data which contains { data: [...], count: ... }
      if (current !== supplierSeq.current) return;
      if (response?.data) {
        setSuppliers(Array.isArray(response.data) ? response.data : []);
      } else if (Array.isArray(response)) {
        // Fallback if response is directly an array
        setSuppliers(response);
      } else {
        setSuppliers([]);
      }
    } catch (err) {
      if (current === supplierSeq.current) { setSuppliers([]); setSupplierError(true); }
    } finally {
      if (current === supplierSeq.current) setLoadingSuppliers(false);
    }
  };

  const fetchPurchases = async () => {
    const current = ++requestSeq.current;
    try {
      setLoading(true);
      setError(null);
      const params = { page, per_page: 30 };
      
      // Add query search
      if (filters.query) {
        params.query = filters.query;
      }
      
      // Add status filter (can be array or single value)
      if (filters.statuses && filters.statuses.length > 0) {
        params.status = filters.statuses.length === 1 ? filters.statuses[0] : filters.statuses;
      }
      
      const response = await purchasesApi.getPurchases(params);
      if (current !== requestSeq.current) return;
      if (response?.data?.success && Array.isArray(response.data.data)) {
        setPurchases(response.data.data);
        setLoadedCompanyId(companyId);
        setTotal(response.data.meta?.total ?? response.data.data.length);
        setSummary(response.data.meta?.summary || null);
      } else {
        setError("inventoryPurchases.loadFailed");
      }
    } catch (err) {
      if (current === requestSeq.current) setError("inventoryPurchases.loadFailed");
    } finally {
      if (current === requestSeq.current) setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (createPending || createInFlightCompany.current === companyId || recoveryChecking) return;
    if (activeAttempt && !recoveryChecked) { setCreateError("inventoryPurchases.checkBeforeRetry"); return; }
    if (!activeAttempt && !newPurchase.supplier_id) {
      setCreateError("inventoryPurchases.supplierRequired");
      return;
    }
    createInFlightCompany.current = companyId;
    let attempt = activeAttempt;
    if (!attempt) {
      const saved = readPurchaseCreateAttempt(companyId);
      if (saved) {
        createInFlightCompany.current = null;
        setStoredAttempt(saved);
        setCreateError("inventoryPurchases.checkBeforeRetry");
        return;
      }
      try {
        attempt = startPurchaseCreateAttempt(companyId, newPurchase, selectedSupplier?.displayTitle || selectedSupplier?.name || "");
        setStoredAttempt(attempt);
      } catch (_) { createInFlightCompany.current = null; setCreateError("inventoryPurchases.storageUnavailable"); return; }
    }
    setCreatePending(true); setCreateError(null); setCreateDefinitelyRejected(false);
    try {
      const response = await purchasesApi.createPurchase(attempt.payload);

      if (response?.data?.success && response.data.data?.id) {
        clearPurchaseCreateAttempt(companyId);
        if (activeCompanyRef.current !== companyId) return;
        setStoredAttempt(null); setRecoveryChecked(false); setCreateDefinitelyRejected(false);
        setCreateDialogOpen(false);
        setNewPurchase({ supplier_id: "", purchase_date: osloToday(), delivery_date: "", reference: "" });
        setSelectedSupplier(null);
        setSupplierSearchQuery("");
        fetchPurchases();
        navigate(`/dashboard/inventory/purchases/${response.data.data.id}`);
        toast.success(t("inventoryPurchases.created"));
      } else {
        if (activeCompanyRef.current === companyId) setCreateError("inventoryPurchases.createUnknown");
      }
    } catch (err) {
      if (activeCompanyRef.current !== companyId) return;
      if (err?.response?.status === 409) setCreateError("inventoryPurchases.payloadConflict");
      else if ([400, 403, 422].includes(err?.response?.status)) {
        setCreateError("inventoryPurchases.createFailed");
        setCreateDefinitelyRejected(err.response.status === 422 && (
          err.response.data?.problem?.outcome === "not_applied"
          || (!err.response.data?.problem && Array.isArray(err.response.data?.errors))
        ));
      }
      else setCreateError("inventoryPurchases.createUnknown");
    } finally {
      if (createInFlightCompany.current === companyId) createInFlightCompany.current = null;
      if (activeCompanyRef.current === companyId) setCreatePending(false);
    }
  };

  const checkUnknownPurchase = async () => {
    if (!activeAttempt || recoveryChecking) return;
    const sequence = ++recoverySeq.current;
    setRecoveryChecking(true); setCreateError(null);
    try {
      const response = await purchasesApi.getPurchases({ page: 1, per_page: 100 });
      if (!response?.data?.success || !Array.isArray(response.data.data)) throw new Error("Invalid purchase list");
      if (sequence !== recoverySeq.current || activeCompanyRef.current !== companyId) return;
      setRecoveryCandidates(response.data.data.filter((purchase) =>
        String(purchase.supplier_id || purchase.supplier?.id) === String(activeAttempt.payload.supplier_id)
        && purchase.purchase_date === activeAttempt.payload.purchase_date
        && (purchase.reference || "") === activeAttempt.payload.reference
      ).slice(0, 3));
      setRecoveryChecked(true);
    } catch (_) { if (sequence === recoverySeq.current && activeCompanyRef.current === companyId) setCreateError("inventoryPurchases.checkFailed"); }
    finally { if (sequence === recoverySeq.current && activeCompanyRef.current === companyId) setRecoveryChecking(false); }
  };

  const abandonUnknownPurchase = () => {
    if (!activeAttempt || !createDefinitelyRejected || createPending) return;
    clearPurchaseCreateAttempt(companyId); setStoredAttempt(null); setRecoveryChecked(false);
    setRecoveryCandidates([]); setCreateError(null); setCreateDefinitelyRejected(false); setSelectedSupplier(null);
    setNewPurchase({ supplier_id: "", purchase_date: osloToday(), delivery_date: "", reference: "" });
  };

  const clearFilters = () => {
    setFilters({ query: "", statuses: [] });
    setPage(1);
    const params = new URLSearchParams(searchParams);
    params.delete("query"); params.delete("status"); params.delete("page");
    setSearchParams(params, { replace: true });
  };

  const selectStatusTab = (statuses) => {
    const next = { ...filters, statuses };
    setFilters(next);
    setPage(1);
    const params = new URLSearchParams(searchParams);
    params.delete("status"); params.delete("page");
    statuses.forEach((status) => params.append("status", status));
    setSearchParams(params, { replace: true });
  };

  const statusTabs = [
    { key: "all", statuses: [], count: summary?.all },
    { key: "draft", statuses: ["draft"], count: summary?.draft },
    { key: "openTab", statuses: OPEN_STATUSES, count: summary?.open },
    { key: "received", statuses: ["received"], count: summary?.received },
  ];
  const statusIsSelected = (statuses) => JSON.stringify(filters.statuses || []) === JSON.stringify(statuses);

  return (
    <>
      <DashboardHeader
        variant="list"
        title={t("inventoryPurchases.title")}
        listCount={loadedCompanyId === companyId ? total : null}
        mainButton={{ label: t("inventoryPurchases.new"), icon: PlusIcon, hidden: !canCreate,
          action: () => { setCreateError(activeAttempt ? "inventoryPurchases.createUnknown" : null); setCreateDialogOpen(true); } }}
      />
      <main className="min-h-full bg-slate-50 p-4 pb-24 md:p-6 xl:p-8"><div className="mx-auto max-w-[1440px] space-y-5">
        <div className="rounded-2xl border border-slate-200 bg-white px-5 py-5 shadow-sm sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[.18em] text-[#1463C7]">{t("inventoryPurchases.eyebrow")}</p>
          <p className="mt-2 text-sm text-slate-600">{t("inventoryPurchases.subtitle")}</p>
        </div>
        {summary && <div className="grid gap-3 sm:grid-cols-3" aria-label={t("inventoryPurchases.overview")}>
          {[
            { key: "openOrders", value: summary.open, detail: canCreate ? (summary.open_amount == null ? t("inventoryPurchases.unknownAmount") : `${formatMoney(summary.open_amount)} ${t("inventoryPurchases.exVat")}`) : t("inventoryPurchases.activeOrders"), icon: ShoppingCartIcon, color: "text-blue-700 bg-blue-50" },
            { key: "draftOrders", value: summary.draft, detail: t("inventoryPurchases.notSent"), icon: DocumentTextIcon, color: "text-slate-700 bg-slate-100" },
            { key: "overdueOrders", value: summary.overdue, detail: t("inventoryPurchases.pastDelivery"), icon: ExclamationTriangleIcon, color: "text-amber-800 bg-amber-50" },
          ].map((card) => <div key={card.key} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"><div className="flex items-start justify-between"><p className="text-xs font-bold uppercase tracking-wider text-slate-500">{t(`inventoryPurchases.${card.key}`)}</p><span className={`rounded-xl p-2 ${card.color}`}><card.icon className="h-5 w-5" /></span></div><p className="mt-2 text-3xl font-bold tabular-nums text-slate-950">{card.value ?? "—"}</p><p className="mt-1 text-sm text-slate-500">{card.detail}</p></div>)}
        </div>}
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex gap-1 overflow-x-auto border-b border-slate-200 px-3 sm:px-5" role="tablist" aria-label={t("inventoryPurchases.statusTabs")}>
            {statusTabs.map((tab) => <button key={tab.key} type="button" role="tab" aria-selected={statusIsSelected(tab.statuses)} onClick={() => selectStatusTab(tab.statuses)} className={`shrink-0 border-b-2 px-3 py-3 text-sm font-semibold transition-colors ${statusIsSelected(tab.statuses) ? "border-[#1463C7] text-[#0B3A78]" : "border-transparent text-slate-500 hover:text-slate-900"}`}>{t(`inventoryPurchases.tab.${tab.key}`)} <span className="ml-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-xs tabular-nums text-slate-600">{tab.count ?? "—"}</span></button>)}
          </div>
          {/* ModuleFilter for search and filtering */}
          <div className="p-3 sm:p-5">
            <ModuleFilter
              onFiltersChange={(next) => {
                const normalized = { query: (next.query || "").trim(), statuses: next.statuses || [] };
                if (JSON.stringify(normalized) === JSON.stringify(filters)) return;
                setFilters(normalized);
                setPage(1);
                const params = new URLSearchParams(searchParams);
                params.delete("query"); params.delete("status"); params.delete("page");
                if (normalized.query) params.set("query", normalized.query);
                normalized.statuses.forEach((status) => params.append("status", status));
                setSearchParams(params, { replace: true });
              }}
              initialFilters={filters}
              acceptedStatuses={["draft", "sent", "confirmed", "received", "cancelled"]}
              searchPlaceholder="inventoryPurchases.search"
              filterTitle="inventoryPurchases.filterStatus"
              filterCard
            />
          </div>
        </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,.04),0_6px_22px_rgba(0,0,0,.07)]">
            {error ? <ResourceError errors={[t(error)]} onRetry={fetchPurchases} /> : loading || loadedCompanyId !== companyId ? <div className="p-10"><ResourceLoading label={t("inventoryPurchases.loading")} /></div> : purchases.length > 0 ? (
              <div className="overflow-x-auto"><table className="w-full min-w-[900px] text-left text-sm"><thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500"><tr>
                {["number", "supplier", "project", "status", "deliveryDate", ...(canCreate ? ["amount"] : []), "open"].map((key) => <th key={key} scope="col" className="px-4 py-3">{t(`inventoryPurchases.${key}`)}</th>)}
              </tr></thead><tbody className="divide-y divide-slate-100">{purchases.map((purchase) => <tr key={purchase.id} className="hover:bg-slate-50">
                <td className="px-4 py-4"><div className="font-bold text-slate-900">{purchase.purchase_number || `#${purchase.id}`}</div><div className="mt-1 text-xs text-slate-500">{formatDate(purchase.purchase_date)}{purchase.created_by?.name ? ` · ${purchase.created_by.name}` : ""}</div></td>
                <td className="px-4 py-4"><div className="font-semibold text-slate-800">{purchase.supplier?.displayTitle || purchase.supplier?.name || "—"}</div><div className="mt-1 text-xs text-slate-500">{t("inventoryPurchases.lineCount", { count: purchase.items?.length || 0 })}</div></td>
                <td className="px-4 py-4 text-slate-700">{purchase.project ? <><div className="font-medium">{purchase.project.name}</div><div className="text-xs text-slate-500">{purchase.project.project_number}</div></> : <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3"><span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusColor[purchase.status] || statusColor.draft}`}>{t(`inventoryPurchases.status.${purchase.status}`, { defaultValue: purchase.status || "—" })}</span></td>
                <td className="px-4 py-3 whitespace-nowrap tabular-nums text-slate-600">{formatDate(purchase.delivery_date)}{OPEN_STATUSES.includes(purchase.status) && purchase.delivery_date && purchase.delivery_date < osloToday() && <span className="ml-2 rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-rose-700">{t("inventoryPurchases.overdue")}</span>}</td>
                {canCreate && <td className="px-4 py-3 whitespace-nowrap text-right font-semibold tabular-nums text-slate-900">{formatMoney(purchase.total_amount, purchase.currency_code || "NOK")}</td>}
                <td className="px-4 py-3"><button type="button" onClick={() => navigate(`/dashboard/inventory/purchases/${purchase.id}`)} className="inline-flex min-h-[40px] items-center gap-2 rounded-lg px-2 font-semibold text-[#1463C7] hover:bg-blue-50"><ArrowTopRightOnSquareIcon className="h-4 w-4" />{t("inventoryPurchases.open")}</button></td>
              </tr>)}</tbody></table></div>
            ) : (
              <div className="px-5 py-12 text-center">
                <ShoppingCartIcon className="mx-auto mb-4 h-12 w-12 text-slate-400" />
                <h3 className="mb-2 text-lg font-bold text-slate-900">{t("inventoryPurchases.empty")}</h3>
                <p className="mb-4 text-sm text-slate-500">{t(filters.query || filters.statuses.length ? "inventoryPurchases.emptyFiltered" : "inventoryPurchases.emptyHint")}</p>
                {(filters.query || filters.statuses.length > 0) && <button type="button" className="min-h-[44px] rounded-xl border border-slate-200 px-5 text-sm font-semibold text-[#1463C7] hover:bg-slate-50" onClick={clearFilters}>{t("inventoryPurchases.clearFilters")}</button>}
                {canCreate && !filters.query && filters.statuses.length === 0 && <button type="button" className="min-h-[44px] rounded-xl bg-[#1463C7] px-5 text-sm font-semibold text-white" onClick={() => { setCreateError(activeAttempt ? "inventoryPurchases.createUnknown" : null); setCreateDialogOpen(true); }}>{t("inventoryPurchases.new")}</button>}
              </div>
            )}
            {loadedCompanyId === companyId && total > 0 && <div className="border-t border-slate-100 px-5 py-3"><PMPagination page={page} perPage={30} totalCount={total} currentCount={purchases.length} onPageChange={(next) => { setPage(next); const params = new URLSearchParams(searchParams); if (next === 1) params.delete("page"); else params.set("page", String(next)); setSearchParams(params, { replace: true }); }} /></div>}
          </div>
      </div></main>

      <Dialog open={createDialogOpen && canCreate && loadedCompanyId === companyId} onClose={() => { if (!createPending) setCreateDialogOpen(false); }} className="relative z-[100]">
        <div className="fixed inset-0 bg-slate-900/40" aria-hidden="true" />
        <div className="fixed inset-0 overflow-y-auto"><div className="flex min-h-full items-end justify-center sm:items-center sm:p-5">
          <Dialog.Panel className="flex w-full max-w-xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl sm:border sm:border-slate-200">
            <div className="border-b border-slate-100 px-5 py-5 sm:px-6"><Dialog.Title className="text-xl font-bold text-slate-900">{activeAttempt ? t("inventoryPurchases.recoveryTitle") : t("inventoryPurchases.createTitle")}</Dialog.Title><p className="mt-1 text-sm text-slate-500">{activeAttempt ? t("inventoryPurchases.recoveryHint") : t("inventoryPurchases.createHint")}</p></div>
            <form onSubmit={(event) => { event.preventDefault(); handleCreate(); }}>
              <div className="max-h-[70vh] space-y-5 overflow-y-auto bg-slate-50 p-5 sm:p-6">
                {activeAttempt && <div role="status" className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"><p className="font-semibold">{t("inventoryPurchases.pendingAttempt")}</p><p className="mt-1">{t("inventoryPurchases.pendingSummary", { supplier: activeAttempt.supplierName || `#${activeAttempt.payload.supplier_id}`, date: activeAttempt.payload.purchase_date, reference: activeAttempt.payload.reference || "—" })}</p></div>}
                <div><label htmlFor="purchase-supplier" className="block text-sm font-semibold text-slate-700">{t("inventoryPurchases.supplier")} *</label>
                  {activeAttempt ? <p id="purchase-supplier" className="mt-1 flex min-h-[44px] items-center rounded-xl border border-slate-200 bg-slate-100 px-3 text-sm text-slate-700">{activeAttempt.supplierName || `#${activeAttempt.payload.supplier_id}`}</p> : <>
                    <input id="purchase-supplier" type="search" autoFocus className="mt-2 min-h-[44px] w-full rounded-xl border border-slate-300 bg-white px-3 text-sm focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-blue-100" placeholder={t("inventoryPurchases.searchSupplier")} value={supplierSearchQuery} onChange={(event) => { setSupplierSearchQuery(event.target.value); setSelectedSupplier(null); setNewPurchase((old) => ({ ...old, supplier_id: "" })); }} disabled={createPending} />
                    <div className="mt-3 max-h-[43vh] space-y-2 overflow-y-auto" role="listbox" aria-label={t("inventoryPurchases.supplier")}>
                      {loadingSuppliers && <p className="px-2 py-3 text-sm text-slate-500">{t("inventoryPurchases.loadingSuppliers")}</p>}
                      {!loadingSuppliers && suppliers.length === 0 && !supplierError && <p className="px-2 py-3 text-sm text-slate-500">{t("inventoryPurchases.noSuppliers")}</p>}
                      {!loadingSuppliers && suppliers.map((supplier) => <button key={supplier.id} type="button" role="option" aria-selected={String(selectedSupplier?.id) === String(supplier.id)} onClick={() => { setSelectedSupplier(supplier); setNewPurchase((old) => ({ ...old, supplier_id: String(supplier.id) })); setCreateError(null); }} className={`flex min-h-[62px] w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors ${String(selectedSupplier?.id) === String(supplier.id) ? "border-[#1463C7] bg-blue-50" : "border-slate-200 bg-white hover:border-blue-200 hover:bg-slate-50"}`}><span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-blue-100 text-xs font-bold text-blue-800">{(supplier.displayTitle || supplier.name || "?").slice(0, 2).toUpperCase()}</span><span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold text-slate-900">{supplier.displayTitle || supplier.name}</span>{supplier.email && <span className="block truncate text-xs text-slate-500">{supplier.email}</span>}</span><span aria-hidden="true" className={`h-4 w-4 rounded-full border-2 ${String(selectedSupplier?.id) === String(supplier.id) ? "border-[#1463C7] bg-[#1463C7] shadow-[inset_0_0_0_3px_white]" : "border-slate-300"}`} /></button>)}
                    </div>
                  </>}
                  {supplierError && <p role="alert" className="mt-2 text-xs text-rose-700">{t("inventoryPurchases.supplierLoadFailed")}</p>}
                </div>
                {createError && <p role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">{t(createError)}</p>}
                {activeAttempt && recoveryChecked && <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900"><p>{t("inventoryPurchases.listChecked")}</p>{recoveryCandidates.length > 0 && <div className="mt-3 space-y-2"><p className="font-semibold">{t("inventoryPurchases.possibleMatches")}</p>{recoveryCandidates.map((candidate) => <button key={candidate.id} type="button" className="block min-h-[40px] font-semibold underline" onClick={() => navigate(`/dashboard/inventory/purchases/${candidate.id}`)}>{candidate.purchase_number || `#${candidate.id}`}</button>)}</div>}</div>}
              </div>
              <div className="sticky bottom-0 flex flex-wrap gap-3 border-t border-slate-100 bg-white px-5 py-4 pb-[max(16px,env(safe-area-inset-bottom))] sm:justify-end sm:px-6"><button type="button" className="min-h-[44px] flex-1 rounded-xl border border-slate-200 px-5 text-sm font-semibold text-slate-700 sm:flex-none" onClick={() => setCreateDialogOpen(false)} disabled={createPending}>{t("inventoryPurchases.cancel")}</button>{activeAttempt ? <><button type="button" className="min-h-[44px] flex-1 rounded-xl border border-slate-200 px-4 text-sm font-semibold text-[#0B3A78] disabled:opacity-50 sm:flex-none" disabled={recoveryChecking || createPending} onClick={checkUnknownPurchase}>{recoveryChecking ? t("inventoryPurchases.checking") : t("inventoryPurchases.checkList")}</button>{createDefinitelyRejected && <button type="button" className="min-h-[44px] flex-1 rounded-xl border border-slate-200 px-4 text-sm font-semibold text-slate-700 sm:flex-none" disabled={createPending} onClick={abandonUnknownPurchase}>{t("inventoryPurchases.newAttempt")}</button>}{recoveryChecked && <button type="button" className="min-h-[44px] flex-1 rounded-xl bg-[#1463C7] px-4 text-sm font-semibold text-white disabled:opacity-50 sm:flex-none" disabled={createPending} onClick={handleCreate}>{createPending ? t("inventoryPurchases.creating") : t("inventoryPurchases.retrySame")}</button>}</> : <button type="submit" className="min-h-[44px] flex-1 rounded-xl bg-[#1463C7] px-5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 sm:flex-none" disabled={createPending || !newPurchase.supplier_id}>{createPending ? t("inventoryPurchases.creating") : t("inventoryPurchases.continue")}</button>}</div>
            </form>
          </Dialog.Panel>
        </div></div>
      </Dialog>
    </>
  );
};
